/**
 * Option values for the official travel wizard.
 *
 * The values are stored verbatim on the request and are what the backend keys
 * its Serbian document labels off, so they must not be localised or renamed —
 * only their displayed labels are translated.
 */

export const TRAVEL_DOMESTIC_CURRENCY = 'RSD';

// Currencies a trip abroad can be settled in: those on the NBS exchange rate
// list, so each one has a middle rate to convert with. Uppercase on purpose:
// the calculation splits domestic from foreign by comparing against 'RSD'.
export const TRAVEL_ABROAD_CURRENCIES = [
  'EUR', 'USD', 'CHF', 'GBP', 'PLN', 'CZK', 'HUF', 'RON', 'BAM', 'MKD',
  'SEK', 'NOK', 'DKK', 'TRY', 'RUB', 'CNY', 'JPY', 'AUD', 'CAD', 'AED',
  'INR', 'KWD', 'BYN',
];

export const TRANSPORT_TYPES = ['Car', 'Bus', 'Airplane', 'Train', 'Other'];

export const PAID_FOOD_TYPES = [
  'Breakfast',
  'Lunch',
  'Dinner',
  'Breakfast&Lunch&Dinner',
  'Breakfast&Lunch',
  'Lunch&Dinner',
  'Dinner&Breakfast',
  'None',
];

export const TRAVEL_INVOICE_TYPES = [
  'Accommodation',
  'Transportation',
  'Other',
];

// What a domestic receipt paid for, as far as the AB journal goes: fuel, tolls
// and parking book to their own accounts, anything else ('') to travel costs.
export const TRAVEL_INVOICE_COST_KINDS = ['', 'Fuel', 'Toll', 'Parking'];

// Suggested purposes of travel. They only seed the input — the field stays free
// text, because no fixed list survives contact with what people actually travel
// for.
export const TRAVEL_REASON_KEYS = [
  'clientMeeting',
  'businessMeeting',
  'conference',
  'training',
  'fair',
  'fieldWork',
];

// ISO 3166-1 alpha-2 codes. Only the codes are kept here; the names come from
// `Intl.DisplayNames` in the viewer's language, so the picker is translated
// without carrying 250 country names per language.
export const COUNTRY_CODES = [
  'AD', 'AE', 'AF', 'AG', 'AI', 'AL', 'AM', 'AO', 'AQ', 'AR', 'AS', 'AT', 'AU',
  'AW', 'AX', 'AZ', 'BA', 'BB', 'BD', 'BE', 'BF', 'BG', 'BH', 'BI', 'BJ', 'BL',
  'BM', 'BN', 'BO', 'BQ', 'BR', 'BS', 'BT', 'BV', 'BW', 'BY', 'BZ', 'CA', 'CC',
  'CD', 'CF', 'CG', 'CH', 'CI', 'CK', 'CL', 'CM', 'CN', 'CO', 'CR', 'CU', 'CV',
  'CW', 'CX', 'CY', 'CZ', 'DE', 'DJ', 'DK', 'DM', 'DO', 'DZ', 'EC', 'EE', 'EG',
  'EH', 'ER', 'ES', 'ET', 'FI', 'FJ', 'FK', 'FM', 'FO', 'FR', 'GA', 'GB', 'GD',
  'GE', 'GF', 'GG', 'GH', 'GI', 'GL', 'GM', 'GN', 'GP', 'GQ', 'GR', 'GS', 'GT',
  'GU', 'GW', 'GY', 'HK', 'HM', 'HN', 'HR', 'HT', 'HU', 'ID', 'IE', 'IL', 'IM',
  'IN', 'IO', 'IQ', 'IR', 'IS', 'IT', 'JE', 'JM', 'JO', 'JP', 'KE', 'KG', 'KH',
  'KI', 'KM', 'KN', 'KP', 'KR', 'KW', 'KY', 'KZ', 'LA', 'LB', 'LC', 'LI', 'LK',
  'LR', 'LS', 'LT', 'LU', 'LV', 'LY', 'MA', 'MC', 'MD', 'ME', 'MF', 'MG', 'MH',
  'MK', 'ML', 'MM', 'MN', 'MO', 'MP', 'MQ', 'MR', 'MS', 'MT', 'MU', 'MV', 'MW',
  'MX', 'MY', 'MZ', 'NA', 'NC', 'NE', 'NF', 'NG', 'NI', 'NL', 'NO', 'NP', 'NR',
  'NU', 'NZ', 'OM', 'PA', 'PE', 'PF', 'PG', 'PH', 'PK', 'PL', 'PM', 'PN', 'PR',
  'PS', 'PT', 'PW', 'PY', 'QA', 'RE', 'RO', 'RS', 'RU', 'RW', 'SA', 'SB', 'SC',
  'SD', 'SE', 'SG', 'SH', 'SI', 'SJ', 'SK', 'SL', 'SM', 'SN', 'SO', 'SR', 'SS',
  'ST', 'SV', 'SX', 'SY', 'SZ', 'TC', 'TD', 'TF', 'TG', 'TH', 'TJ', 'TK', 'TL',
  'TM', 'TN', 'TO', 'TR', 'TT', 'TV', 'TW', 'TZ', 'UA', 'UG', 'UM', 'US', 'UY',
  'UZ', 'VA', 'VC', 'VE', 'VG', 'VI', 'VN', 'VU', 'WF', 'WS', 'YE', 'YT', 'ZA',
  'ZM', 'ZW',
];

// The travel order document is issued in Serbian, so the country is stored
// under its Serbian name regardless of the language the picker was used in.
// Latin script, because plain 'sr' resolves to Cyrillic while the rest of the
// application is in Latin.
export const DOCUMENT_LANGUAGE = 'sr-Latn';

// Same reason: the picker must not show Cyrillic country names inside an
// otherwise Latin Serbian interface.
const DISPLAY_LOCALES = { sr: 'sr-Latn' };

/**
 * Builds the country options for a language, sorted the way that language
 * sorts. Codes the runtime has no name for are dropped rather than shown raw.
 *
 * @param {string} language - Language to label the countries in.
 * @returns {Array<{value: string, label: string}>}
 */
export const buildCountryOptions = (language) => {
  const locale = DISPLAY_LOCALES[language] || language;

  let displayNames;

  try {
    displayNames = new Intl.DisplayNames([locale], { type: 'region' });
  } catch {
    displayNames = null;
  }

  return COUNTRY_CODES.map((value) => {
    let label = value;

    try {
      label = displayNames?.of(value) || value;
    } catch {
      label = value;
    }

    return { value, label };
  })
    .filter(({ value, label }) => label !== value)
    .sort((a, b) => a.label.localeCompare(b.label, locale));
};

/**
 * The Serbian name of a country code, for storing on the travel order.
 *
 * @param {string} code - ISO 3166-1 alpha-2 code.
 * @returns {string} Serbian country name, or the code when unknown.
 */
// The order keeps the Serbian name for the document; on screen the country is
// named in the language the user is reading.
export const countryNameForDisplay = (code, language, fallback = '') => {
  if (!code) return fallback;

  try {
    return (
      new Intl.DisplayNames([language], { type: 'region' }).of(code) ||
      fallback ||
      code
    );
  } catch {
    return fallback || code;
  }
};

export const countryNameForDocument = (code) => {
  if (!code) return '';

  try {
    return (
      new Intl.DisplayNames([DOCUMENT_LANGUAGE], { type: 'region' }).of(code) ||
      code
    );
  } catch {
    return code;
  }
};

// Rows of the settlement table that carry a money amount, in the order the
// travel order document prints them. `country` marks the domestic bucket so the
// table can suffix the right currency.
export const CALCULATION_AMOUNT_ROWS = [
  // Shown only when the order carries a reduction, so a full per diem is not
  // padded with two zero rows.
  { key: 'grossDailyWageForCountry', country: true, onlyWithReduction: true },
  { key: 'grossDailyWageForAbroad', country: false, onlyWithReduction: true },
  { key: 'dailyWageReductionCountry', country: true, onlyWithReduction: true },
  { key: 'dailyWageReductionAbroad', country: false, onlyWithReduction: true },
  { key: 'totalDailyWageForCountry', country: true, highlighted: true },
  { key: 'totalDailyWageForAbroad', country: false, highlighted: true },
  { key: 'accommodationCostsByInvoicesCountry', country: true },
  { key: 'accommodationCostsByInvoicesAbroad', country: false },
  { key: 'transportationCostsByInvoicesCountry', country: true },
  { key: 'transportationCostsByInvoicesAbroad', country: false },
  { key: 'otherCostsByInvoicesCountry', country: true },
  { key: 'otherCostsByInvoicesAbroad', country: false },
  { key: 'totalCostsCountry', country: true, highlighted: true },
  { key: 'totalCostsAbroad', country: false, highlighted: true },
  { key: 'totalCalculationCountry', country: true },
  { key: 'totalCalculationAbroad', country: false },
  { key: 'totalPaymentInAdvanceCountry', country: true },
  { key: 'totalPaymentInAdvanceAbroad', country: false },
  { key: 'totalPaidWithCompanyCardCountry', country: true },
  { key: 'totalPaidWithCompanyCardAbroad', country: false },
  { key: 'totalAmountForPaymentCountry', country: true, highlighted: true },
  { key: 'totalAmountForPaymentAbroad', country: false, highlighted: true },
];

// Rows that are plain counts rather than money.
export const CALCULATION_COUNT_ROWS = [
  'totalNumberOfHours',
  'numberOfHoursCountry',
  'numberOfHoursAbroad',
  'numberOfDailyWageForCountry',
  'numberOfDailyWageForAbroad',
];
